import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationListResponse,
  NotificationRecord,
} from '@billing/validation';
import { getRequest, putRequest } from 'api/utils/HttpMethods';
import queryString from 'api/utils/queryStringBuilder';

export const notificationQueryKeys = {
  all: ['notifications'] as const,
  list: (page: number, pageSize: number, unreadOnly: boolean) => [
    ...notificationQueryKeys.all,
    page,
    pageSize,
    unreadOnly,
  ],
};

export const useListNotifications = ({
  page = 0,
  pageSize = 20,
  unreadOnly = false,
}: {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
}) =>
  useQuery<NotificationListResponse, Error>({
    queryKey: notificationQueryKeys.list(page, pageSize, unreadOnly),
    queryFn: async () => {
      const result = await getRequest<NotificationListResponse>(
        'QBilling',
        `/resource/notification${queryString({
          page,
          pageSize,
          unreadOnly,
        })}`
      );
      if (!result) throw new Error('Failed to load notifications');
      return result;
    },
  });

export const useSetNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationRecord,
    Error,
    { notificationID: string; read: boolean }
  >({
    mutationFn: async (input) => {
      const result = await putRequest<NotificationRecord, typeof input>(
        'QBilling',
        '/resource/notification',
        input
      );
      if (!result) throw new Error('Notification not found');
      return result;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all }),
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation<{ updated: number }, Error>({
    mutationFn: async () => {
      const result = await putRequest<{ updated: number }, { all: true }>(
        'QBilling',
        '/resource/notification',
        { all: true }
      );
      if (!result) throw new Error('Failed to mark notifications read');
      return result;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all }),
  });
};
